import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

const exportPresets = [
  { key: "all", label: "All dates" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_6_months", label: "Last 6 months" },
  { key: "this_financial_year", label: "This financial year" },
  { key: "custom", label: "Custom range" },
];

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseInvoiceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const normalized = String(value).trim();
  if (!normalized || normalized === "N/A") return null;

  const isoDate = new Date(normalized);
  if (!Number.isNaN(isoDate.getTime())) return isoDate;

  const parts = normalized.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (!parts) return null;

  const first = Number(parts[1]);
  const second = Number(parts[2]);
  const year = Number(parts[3].length === 2 ? `20${parts[3]}` : parts[3]);
  const month = first > 12 ? second - 1 : first - 1;
  const day = first > 12 ? first : second;
  const parsed = new Date(year, month, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getPresetRange = (preset, customStart, customEnd) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  if (preset === "this_month") {
    return {
      start: startOfDay(new Date(currentYear, currentMonth, 1)),
      end: endOfDay(new Date(currentYear, currentMonth + 1, 0)),
    };
  }

  if (preset === "last_month") {
    return {
      start: startOfDay(new Date(currentYear, currentMonth - 1, 1)),
      end: endOfDay(new Date(currentYear, currentMonth, 0)),
    };
  }

  if (preset === "this_quarter") {
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    return {
      start: startOfDay(new Date(currentYear, quarterStartMonth, 1)),
      end: endOfDay(new Date(currentYear, quarterStartMonth + 3, 0)),
    };
  }

  if (preset === "last_6_months") {
    return {
      start: startOfDay(new Date(currentYear, currentMonth - 5, 1)),
      end: endOfDay(now),
    };
  }

  if (preset === "this_financial_year") {
    const fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    return {
      start: startOfDay(new Date(fyStartYear, 3, 1)),
      end: endOfDay(new Date(fyStartYear + 1, 2, 31)),
    };
  }

  if (preset === "custom") {
    return {
      start: customStart ? startOfDay(new Date(customStart)) : null,
      end: customEnd ? endOfDay(new Date(customEnd)) : null,
    };
  }

  return { start: null, end: null };
};

const csvEscape = (value) => {
  const text = value === undefined || value === null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

function InvoiceCsvExport({
  isOpen,
  onClose,
  title = "Custom CSV Export",
  columns = [],
  fetchRows,
  getRowDate,
  filenamePrefix = "invoice-report",
  accent = "#2563eb",
}) {
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState(toInputDate(new Date()));
  const [customEnd, setCustomEnd] = useState(toInputDate(new Date()));
  const [selectedColumns, setSelectedColumns] = useState(() =>
    columns.reduce((acc, column) => ({ ...acc, [column.key]: column.defaultSelected !== false }), {})
  );
  const [loading, setLoading] = useState(false);

  const selectedCount = useMemo(
    () => columns.filter((column) => selectedColumns[column.key]).length,
    [columns, selectedColumns]
  );

  if (!isOpen) return null;

  const handleToggleColumn = (key) => {
    setSelectedColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async () => {
    const activeColumns = columns.filter((column) => selectedColumns[column.key]);
    if (!activeColumns.length) {
      toast.error("Please select at least one column.");
      return;
    }

    const range = getPresetRange(preset, customStart, customEnd);
    if (preset === "custom" && (!range.start || !range.end || range.start > range.end)) {
      toast.error("Please select a valid custom date range.");
      return;
    }

    try {
      setLoading(true);
      const rows = await fetchRows();
      const filteredRows = rows.filter((row) => {
        if (!range.start && !range.end) return true;

        const rowDate = parseInvoiceDate(getRowDate(row));
        if (!rowDate) return false;

        return (!range.start || rowDate >= range.start) && (!range.end || rowDate <= range.end);
      });

      if (!filteredRows.length) {
        toast.info("No invoices found for the selected export range.");
        return;
      }

      const csvRows = [
        activeColumns.map((column) => csvEscape(column.label)).join(","),
        ...filteredRows.map((row) =>
          activeColumns
            .map((column) => csvEscape(column.getValue ? column.getValue(row) : row[column.key]))
            .join(",")
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filenamePrefix}-${preset}-${toInputDate(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredRows.length} CSV row${filteredRows.length === 1 ? "" : "s"}.`);
      onClose();
    } catch (error) {
      console.error("CSV export failed:", error);
      toast.error("Failed to export CSV report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ice-overlay" role="dialog" aria-modal="true">
      <style>
        {`
          .ice-overlay {
            position: fixed;
            inset: 0;
            z-index: 1050;
            background: rgba(15, 23, 42, 0.48);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
          }

          .ice-modal {
            width: min(760px, 100%);
            max-height: min(86vh, 760px);
            overflow: auto;
            background: #ffffff;
            border-radius: 22px;
            box-shadow: 0 30px 80px rgba(15, 23, 42, 0.3);
            border: 1px solid #e5e7eb;
          }

          .ice-head,
          .ice-actions {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 18px 20px;
          }

          .ice-head {
            border-bottom: 1px solid #e5e7eb;
          }

          .ice-actions {
            border-top: 1px solid #e5e7eb;
            justify-content: flex-end;
            flex-wrap: wrap;
          }

          .ice-title {
            margin: 0;
            font-size: 20px;
            font-weight: 950;
            color: #0f172a;
          }

          .ice-subtitle {
            margin: 4px 0 0;
            color: #64748b;
            font-size: 13px;
            font-weight: 700;
          }

          .ice-close {
            border: none;
            background: #f1f5f9;
            color: #334155;
            border-radius: 12px;
            width: 38px;
            height: 38px;
            font-size: 22px;
            cursor: pointer;
          }

          .ice-body {
            padding: 20px;
            display: grid;
            gap: 18px;
          }

          .ice-section-title {
            margin: 0 0 10px;
            font-size: 13px;
            color: #334155;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .ice-preset-grid,
          .ice-column-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
          }

          .ice-choice {
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 11px 12px;
            cursor: pointer;
            font-weight: 850;
            color: #334155;
            display: flex;
            gap: 8px;
            align-items: center;
            background: #ffffff;
          }

          .ice-choice-active {
            border-color: ${accent};
            background: ${accent}12;
            color: #0f172a;
          }

          .ice-custom-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }

          .ice-field label {
            display: block;
            font-size: 12px;
            color: #64748b;
            font-weight: 850;
            margin-bottom: 6px;
          }

          .ice-field input {
            width: 100%;
            border: 1px solid #d1d5db;
            border-radius: 12px;
            height: 42px;
            padding: 0 12px;
            font-weight: 800;
            color: #0f172a;
          }

          .ice-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-weight: 950;
            cursor: pointer;
          }

          .ice-btn-light {
            background: #f1f5f9;
            color: #334155;
          }

          .ice-btn-primary {
            background: ${accent};
            color: #ffffff;
          }

          .ice-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @media (max-width: 720px) {
            .ice-preset-grid,
            .ice-column-grid,
            .ice-custom-grid {
              grid-template-columns: 1fr;
            }

            .ice-actions .ice-btn {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="ice-modal">
        <div className="ice-head">
          <div>
            <h3 className="ice-title">{title}</h3>
            <p className="ice-subtitle">Choose date range and columns before downloading.</p>
          </div>

          <button type="button" className="ice-close" onClick={onClose} aria-label="Close export modal">
            ×
          </button>
        </div>

        <div className="ice-body">
          <section>
            <h4 className="ice-section-title">Date range</h4>
            <div className="ice-preset-grid">
              {exportPresets.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setPreset(option.key)}
                  className={`ice-choice ${preset === option.key ? "ice-choice-active" : ""}`}
                >
                  <span>{preset === option.key ? "●" : "○"}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </section>

          {preset === "custom" && (
            <section className="ice-custom-grid">
              <div className="ice-field">
                <label>Start date</label>
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </div>

              <div className="ice-field">
                <label>End date</label>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </div>
            </section>
          )}

          <section>
            <h4 className="ice-section-title">Columns ({selectedCount} selected)</h4>
            <div className="ice-column-grid">
              {columns.map((column) => (
                <label
                  key={column.key}
                  className={`ice-choice ${selectedColumns[column.key] ? "ice-choice-active" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(selectedColumns[column.key])}
                    onChange={() => handleToggleColumn(column.key)}
                  />
                  <span>{column.label}</span>
                </label>
              ))}
            </div>
          </section>
        </div>

        <div className="ice-actions">
          <button type="button" className="ice-btn ice-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ice-btn ice-btn-primary" onClick={handleExport} disabled={loading}>
            {loading ? "Exporting..." : "Download CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InvoiceCsvExport;
